import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetTicketByIdQuery,
  useEditTicketMutation,
} from "@/features/api/supportApi";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const EditSupportTicket = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  /* ================= FETCH TICKET ================= */
  const {
    data: ticket,
    isLoading,
    isError,
  } = useGetTicketByIdQuery(id, { skip: !id });

  /* ================= UPDATE MUTATION ================= */
  const [updateTicket, { isLoading: isUpdating }] =
    useEditTicketMutation();

  /* ================= FORM STATE ================= */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    status: "pending",
  });

  /* ================= PREFILL DATA ================= */
  useEffect(() => {
    if (ticket) {
      setFormData({
        name: ticket.name ?? "",
        email: ticket.email ?? "",
        phone: ticket.phone ?? "",
        subject: ticket.subject ?? "",
        message: ticket.message ?? "",
        status: (ticket.status || "pending").toLowerCase(),
      });
    }
  }, [ticket]);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await updateTicket({
        id,
        data: {
          ...formData,
          status:
            formData.status.charAt(0).toUpperCase() +
            formData.status.slice(1),
        },
      }).unwrap();

      toast.success("Ticket updated successfully");
      navigate("/admin/support/list");
    } catch (err) {
      console.error("Update ticket error:", err);
      toast.error(err?.data?.message || "Failed to update ticket");
    }
  };

  /* ================= UI STATES ================= */
  if (isLoading) {
    return (
      <div className="flex justify-center mt-20">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <div className="text-center mt-20 text-red-500">
        Failed to load ticket
      </div>
    );
  }

  /* ================= RENDER ================= */
  return (
    <div className="flex justify-center mt-10 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Edit Support Ticket</CardTitle>
          <CardDescription>
            Modify ticket details and update status
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <CardContent className="space-y-4">
            {/* Name */}
            <div>
              <Label>Name</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            {/* Email */}
            <div>
              <Label>Email</Label>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            {/* Phone */}
            <div>
              <Label>Phone</Label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            {/* Subject */}
            <div>
              <Label>Subject</Label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full p-3 border rounded-md bg-white"
              >
                <option value="">Select a Subject</option>
                <option value="course">Course Related</option>
                <option value="workpermit">Work Permit</option>
                <option value="studyabroad">Study Abroad</option>
                <option value="website">Website Issues</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <Label>Status</Label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full p-3 border rounded-md bg-white"
              >
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="replied">Replied</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <Label>Message</Label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                required
              />
            </div>
          </CardContent>

          <CardFooter className="flex justify-between gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/support/list")}
            >
              ← Back
            </Button>

            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Ticket"
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default EditSupportTicket;
